/**
 * ToothSVG — anatomically-styled FDI tooth with 5 interactive surfaces and
 * condition-specific overlay icons.
 *
 * We render three layers:
 *   1. Crown silhouette (shape depends on tooth type: incisor / canine /
 *      premolar / molar / primary variants).
 *   2. Surface polygons (hit-testable; painted with the condition's colour when
 *      a condition is applied to that surface — subtle, not overwhelming).
 *   3. Condition overlays — small SVG glyphs stacked at the crown centre for
 *      any whole-tooth condition (crown, RCT, implant, missing…). Each glyph
 *      is a proper icon, not a colour swatch.
 *
 * All tooth graphics share the same 40×64 viewBox so the parent grid stays
 * uniform. Upper and lower teeth are the same graphic mirrored vertically.
 */
import React from "react";

// ---------- Tooth typing helpers ----------
export const TOOTH_NAMES = {
  // Permanent
  1: "Central Incisor", 2: "Lateral Incisor", 3: "Canine",
  4: "First Premolar", 5: "Second Premolar",
  6: "First Molar", 7: "Second Molar", 8: "Third Molar",
  // Primary
  primary: {
    1: "Central Incisor (A)", 2: "Lateral Incisor (B)", 3: "Canine (C)",
    4: "First Molar (D)", 5: "Second Molar (E)",
  },
};

export function toothInfo(fdi) {
  const q = parseInt(fdi[0], 10);
  const n = parseInt(fdi[1], 10);
  const isPrimary = q >= 5;
  const isUpper = q === 1 || q === 2 || q === 5 || q === 6;
  const type =
    n === 1 || n === 2 ? "incisor" :
    n === 3            ? "canine"  :
    !isPrimary && (n === 4 || n === 5) ? "premolar" :
    "molar";
  const name = isPrimary ? TOOTH_NAMES.primary[n] : TOOTH_NAMES[n];
  const surfaces = (type === "incisor" || type === "canine")
    ? ["M", "I", "D", "B", "L"]   // I = incisal
    : ["M", "O", "D", "B", "L"];  // O = occlusal
  return { q, n, isPrimary, isUpper, type, name, surfaces };
}

// ---------- Surface polygons ----------
// Coordinates chosen to look anatomically plausible while remaining clickable.
// A tooth's crown occupies y ∈ [4, 46]; root ∈ [46, 60]. Upper teeth are mirrored
// vertically at render time.
const SURFACE_PATHS = (type) => {
  // Anteriors (incisor/canine): incisal edge is a small strip at the top.
  if (type === "incisor" || type === "canine") {
    return {
      I: "M8 4 H32 V14 H8 Z",       // Incisal
      B: "M8 14 H32 V26 H8 Z",      // Buccal / labial
      L: "M8 34 H32 V46 H8 Z",      // Lingual / palatal
      M: "M8 26 H20 V34 H8 Z",      // Mesial
      D: "M20 26 H32 V34 H20 Z",    // Distal
    };
  }
  // Posteriors (premolar / molar): occlusal is central.
  return {
    O: "M10 18 H30 V30 H10 Z",
    B: "M8 8 H32 V18 H8 Z",
    L: "M8 30 H32 V44 H8 Z",
    M: "M8 18 H10 V30 H8 Z",
    D: "M30 18 H32 V30 H30 Z",
  };
};

// ---------- Crown silhouettes ----------
// Each returns a JSX <path>.
const CrownShape = ({ type, isPrimary, fill, stroke, sw }) => {
  const commonProps = { fill, stroke, strokeWidth: sw, strokeLinejoin: "round" };
  if (type === "incisor") {
    // Trapezoidal crown, slightly narrower at gum line.
    return <path {...commonProps}
      d="M10 6 Q10 4 12 4 H28 Q30 4 30 6 V40 Q30 46 20 47 Q10 46 10 40 Z" />;
  }
  if (type === "canine") {
    // Pointed cusp.
    return <path {...commonProps}
      d="M14 5 Q20 3 26 5 L29 40 Q28 46 20 47 Q12 46 11 40 Z" />;
  }
  if (type === "premolar") {
    // Two cusps hinted at top.
    return <path {...commonProps}
      d="M8 10 Q10 5 14 5 Q16 5 18 8 Q20 5 22 5 Q26 5 32 10 L30 42 Q28 47 20 47 Q12 47 10 42 Z" />;
  }
  // Molar — wider, four cusps hint on top edge, tapered root.
  if (isPrimary) {
    return <path {...commonProps}
      d="M6 12 Q7 6 12 5 Q14 5 15 7 Q18 5 20 7 Q22 5 25 7 Q26 5 28 5 Q33 6 34 12 L32 42 Q30 47 20 47 Q10 47 8 42 Z" />;
  }
  return <path {...commonProps}
    d="M5 14 Q6 6 11 5 Q13 5 14 8 Q17 5 20 8 Q23 5 26 8 Q27 5 29 5 Q34 6 35 14 L33 44 Q30 48 20 48 Q10 48 7 44 Z" />;
};

// Root fingers below the crown.
const RootShape = ({ type, isPrimary }) => {
  const fill = "#F3EFE7";
  const stroke = "#C8BFA7";
  if (type === "incisor" || type === "canine") {
    // Single root.
    return <path fill={fill} stroke={stroke} strokeWidth={0.9}
      d="M17 47 Q17 58 20 61 Q23 58 23 47 Z" />;
  }
  if (type === "premolar") {
    return <path fill={fill} stroke={stroke} strokeWidth={0.9}
      d="M14 47 Q13 58 16 61 L19 61 L19 47 Z M21 47 L21 61 L24 61 Q27 58 26 47 Z" />;
  }
  // Molar — 2 (primary) or 3 roots.
  if (isPrimary) {
    return <path fill={fill} stroke={stroke} strokeWidth={0.9}
      d="M11 47 Q9 58 12 61 L16 61 L17 47 Z M23 47 L24 61 L28 61 Q31 58 29 47 Z" />;
  }
  return <path fill={fill} stroke={stroke} strokeWidth={0.9}
    d="M10 48 Q8 60 11 62 L14 62 L15 48 Z M18 48 L18 62 L22 62 L22 48 Z M25 48 L25 62 L29 62 Q32 60 30 48 Z" />;
};

// ---------- Condition overlays ----------
// Each overlay is drawn *at the crown centre* (cx=20, cy=25) unless otherwise
// stated. Whole-tooth overlays get z-ordered on top of surface fills.
function ConditionOverlays({ tooth, conditions, surfaces }) {
  const cx = 20, cy = 25;
  const glyphs = [];
  const seen = new Set();
  for (const c of conditions) {
    if (seen.has(c.code)) continue;      // one glyph per condition on this tooth
    seen.add(c.code);
    const key = `${c.id || c.code}`;
    const meta = CONDITION_META[c.code];
    if (!meta) continue;
    // Whole-tooth graphics
    switch (c.code) {
      case "crown": {
        // Small crown coronet on top of the tooth
        glyphs.push(
          <g key={key} transform={`translate(${cx-7} 1)`}>
            <path d="M0 5 L3 0 L7 4 L10 0 L13 5 Z" fill="#C89F55" stroke="#8A6E3B" strokeWidth={0.6} />
            <rect x={0} y={5} width={13} height={2.4} fill="#C89F55" stroke="#8A6E3B" strokeWidth={0.6} />
            <circle cx={3} cy={1} r={0.8} fill="#8A6E3B" />
            <circle cx={10} cy={1} r={0.8} fill="#8A6E3B" />
          </g>
        );
        break;
      }
      case "implant": {
        // Screw shape
        glyphs.push(
          <g key={key} transform={`translate(${cx-2.5} 20)`}>
            <rect x={0} y={0} width={5} height={20} rx={0.5} fill="#8A8CD9" stroke="#4E4E9C" strokeWidth={0.5} />
            {[3, 7, 11, 15].map((yy, i) => (
              <line key={i} x1={-0.5} y1={yy} x2={5.5} y2={yy} stroke="#4E4E9C" strokeWidth={0.5} />
            ))}
          </g>
        );
        break;
      }
      case "rct": {
        // Root canal filling — vertical stripes through root
        glyphs.push(
          <g key={key} stroke="#7B4EA8" strokeWidth={1.2} strokeLinecap="round">
            {["incisor","canine"].includes(toothInfo(tooth).type)
              ? <line x1={20} y1={47} x2={20} y2={60} />
              : <>
                  <line x1={15} y1={48} x2={13} y2={60} />
                  <line x1={26} y1={48} x2={27} y2={60} />
                </>
            }
            <circle cx={20} cy={30} r={2.6} fill="#EEE1FF" stroke="#7B4EA8" strokeWidth={1} />
            <text x={20} y={32.5} textAnchor="middle" fontSize={4} fill="#7B4EA8" fontWeight="bold">℞</text>
          </g>
        );
        break;
      }
      case "missing":
      case "extracted": {
        glyphs.push(
          <g key={key}>
            <line x1={6} y1={4} x2={34} y2={48} stroke="#B94A48" strokeWidth={2.5} strokeLinecap="round" />
            <line x1={34} y1={4} x2={6} y2={48} stroke="#B94A48" strokeWidth={2.5} strokeLinecap="round" />
          </g>
        );
        break;
      }
      case "extraction_indicated": {
        // Dashed X — planned
        glyphs.push(
          <g key={key}>
            <line x1={8} y1={8} x2={32} y2={44} stroke="#B33C3C" strokeWidth={1.8} strokeDasharray="3 2" />
            <line x1={32} y1={8} x2={8} y2={44} stroke="#B33C3C" strokeWidth={1.8} strokeDasharray="3 2" />
          </g>
        );
        break;
      }
      case "impacted": {
        glyphs.push(
          <text key={key} x={cx} y={cy+3} textAnchor="middle" fontSize={12} fontWeight="700" fill="#5C4D3D">⚑</text>
        );
        break;
      }
      case "retained_root": {
        glyphs.push(
          <g key={key}>
            <line x1={10} y1={22} x2={30} y2={22} stroke="#8A6E4E" strokeWidth={1.8} strokeDasharray="2 2" />
            <text x={cx} y={44} textAnchor="middle" fontSize={7} fill="#8A6E4E">Rr</text>
          </g>
        );
        break;
      }
      case "mobility": {
        glyphs.push(
          <g key={key} transform={`translate(${cx-4} 40)`}>
            <path d="M0 3 Q2 0 4 3 T8 3" stroke="#F2A65A" strokeWidth={1.2} fill="none" />
            <path d="M0 6 Q2 3 4 6 T8 6" stroke="#F2A65A" strokeWidth={1.2} fill="none" />
          </g>
        );
        break;
      }
      case "fracture": {
        glyphs.push(
          <polyline key={key}
            points={`14,10 18,20 15,30 22,38`}
            fill="none" stroke="#E07A5F" strokeWidth={1.6} />
        );
        break;
      }
      case "veneer": {
        // Bar over labial surface
        glyphs.push(
          <rect key={key} x={9} y={14} width={22} height={10} rx={1.5}
                fill="rgba(217,203,184,0.55)" stroke="#8A785A" strokeWidth={0.6} />
        );
        break;
      }
      case "sealant": {
        glyphs.push(
          <path key={key} d="M12 24 Q20 20 28 24" fill="none"
                stroke="#5B9DB0" strokeWidth={1.5} strokeLinecap="round" />
        );
        break;
      }
      case "bridge_abutment":
      case "pontic": {
        glyphs.push(
          <rect key={key} x={4} y={22} width={32} height={4}
                fill={c.code === "pontic" ? "#5F9E7E" : "none"}
                stroke="#4A7E62" strokeWidth={1.4} strokeLinecap="round" />
        );
        break;
      }
      case "calculus": {
        glyphs.push(
          <g key={key}>
            <circle cx={12} cy={44} r={1.5} fill="#A69982" />
            <circle cx={18} cy={45} r={1.7} fill="#A69982" />
            <circle cx={25} cy={44} r={1.4} fill="#A69982" />
          </g>
        );
        break;
      }
      case "gingival_recession": {
        glyphs.push(
          <path key={key} d="M8 46 Q13 42 20 44 Q27 42 32 46" fill="none"
                stroke="#D28B7A" strokeWidth={1.6} />
        );
        break;
      }
      case "periapical_lesion": {
        glyphs.push(
          <circle key={key} cx={20} cy={56} r={4.5}
                  fill="rgba(176,87,140,0.35)" stroke="#B0578C" strokeWidth={1} strokeDasharray="1.5 1.5" />
        );
        break;
      }
      case "wear":
      case "attrition":
      case "abrasion": {
        glyphs.push(
          <path key={key} d="M10 12 Q20 10 30 12" fill="none"
                stroke="#B8AEA0" strokeWidth={1.4} strokeDasharray="1.5 2" />
        );
        break;
      }
      case "hypoplasia": {
        glyphs.push(
          <g key={key}>
            <circle cx={14} cy={18} r={1.2} fill="none" stroke="#8B7C4E" strokeWidth={0.7} />
            <circle cx={22} cy={22} r={1.4} fill="none" stroke="#8B7C4E" strokeWidth={0.7} />
            <circle cx={28} cy={18} r={1.1} fill="none" stroke="#8B7C4E" strokeWidth={0.7} />
          </g>
        );
        break;
      }
      case "temp_restoration": {
        glyphs.push(
          <rect key={key} x={14} y={22} width={12} height={6} rx={1}
                fill="rgba(244,201,124,0.7)" stroke="#B67F2A" strokeWidth={0.6} strokeDasharray="2 1.5" />
        );
        break;
      }
      case "caries": {
        // Small dark cavities at surface centres
        glyphs.push(
          <g key={key}>
            {(c.surfaces || ["O","I"]).slice(0, 3).map((s, i) => {
              const p = SURFACE_CENTER[s] || { x: 20, y: 25 };
              return <circle key={i} cx={p.x} cy={p.y} r={1.6}
                             fill="#3A0C0B" stroke="#6B1D1B" strokeWidth={0.6} />;
            })}
          </g>
        );
        break;
      }
      case "filling":
      case "amalgam":
      case "composite": {
        // Rounded pill on the surfaces where placed
        glyphs.push(
          <g key={key}>
            {(c.surfaces || ["O"]).map((s, i) => {
              const p = SURFACE_CENTER[s] || { x: 20, y: 25 };
              const color = c.code === "amalgam" ? "#6B7280" : "#374151";
              return <circle key={i} cx={p.x} cy={p.y} r={2}
                             fill={color} opacity={0.85} />;
            })}
          </g>
        );
        break;
      }
      default: break;
    }
  }
  return <>{glyphs}</>;
}

const SURFACE_CENTER = {
  M: { x: 12, y: 25 },
  D: { x: 28, y: 25 },
  B: { x: 20, y: 15 },
  L: { x: 20, y: 38 },
  O: { x: 20, y: 25 },
  I: { x: 20, y: 12 },
};

// ---------- Main component ----------
export default function ToothSVG({
  tooth,
  isSelected,
  selectedSurfaces = [],
  conditions = [],
  onTooth,
  onSurface,
  // When rendered inside an arch that already labels each tooth externally
  // (e.g. the FDI chart), pass `hideLabel` to suppress the built-in micro
  // FDI text — it otherwise renders mirror-inverted in the flipped upper arch.
  hideLabel = false,
}) {
  const info = toothInfo(tooth);
  const surfaces = SURFACE_PATHS(info.type);

  // Fill each surface if that surface has an *incipient* condition (caries,
  // filling, sealant) applied to it — softly tinted.
  const surfaceTint = {};
  for (const c of conditions) {
    const meta = CONDITION_META[c.code];
    if (!meta || meta.whole || !c.surfaces?.length) continue;
    for (const s of c.surfaces) {
      surfaceTint[s] = meta.color + "44"; // 27% opacity
    }
  }

  const missing = conditions.some((c) => c.code === "missing" || c.code === "extracted");

  return (
    <button
      type="button"
      data-testid={`tooth-${tooth}`}
      onClick={() => onTooth?.(tooth)}
      className={`group relative flex flex-col items-center gap-1 focus:outline-none tooth-btn ${missing ? "opacity-70" : ""}`}
      title={`${tooth} · ${info.name}`}>
      {!hideLabel && (
        <span className={`text-[9.5px] font-mono ${isSelected ? "text-clay font-semibold" : "text-ink-soft"}`}>
          {tooth}
        </span>
      )}
      <svg viewBox="0 0 40 64" width="38" height="60"
           className={`overflow-visible transition-transform duration-200 ${isSelected ? "scale-110" : "group-hover:scale-105"}`}
           style={{ transform: info.isUpper ? undefined : "scaleY(-1)" }}>
        {/* Selection halo */}
        {isSelected && (
          <rect x={-1} y={-1} width={42} height={66} rx={8}
                fill="none" stroke="#D06A42" strokeWidth={1.5}
                strokeDasharray="3 3" className="tooth-halo" />
        )}
        {/* Root behind crown */}
        <RootShape type={info.type} isPrimary={info.isPrimary} />
        {/* Crown */}
        <CrownShape type={info.type} isPrimary={info.isPrimary}
                    fill="#FBF7EE" stroke={isSelected ? "#1A362B" : "#B7AA8B"}
                    sw={isSelected ? 1.4 : 0.9} />

        {/* Surface hit paths — clicked individually */}
        {Object.entries(surfaces).map(([s, d]) => (
          <path
            key={s} d={d}
            fill={surfaceTint[s] || "transparent"}
            stroke={selectedSurfaces.includes(s) ? "#1A362B" : "transparent"}
            strokeWidth={1.8}
            className="cursor-pointer transition-colors hover:fill-[rgba(26,54,43,0.08)]"
            onClick={(e) => { e.stopPropagation(); onSurface?.(tooth, s); }}>
            <title>{SURFACE_LABELS[s]}</title>
          </path>
        ))}

        {/* Condition overlays */}
        {!missing && <ConditionOverlays tooth={tooth} conditions={conditions} surfaces={selectedSurfaces} />}
        {/* Draw the extraction X on top of everything if missing */}
        {missing && <ConditionOverlays tooth={tooth}
                    conditions={conditions.filter((c) => c.code === "missing" || c.code === "extracted")}
                    surfaces={[]} />}
      </svg>
    </button>
  );
}

// ---------- Exported metadata ----------
export const CONDITION_META = {
  caries:               { label: "Caries",               color: "#B94A48", group: "diagnosis" },
  filling:              { label: "Composite filling",    color: "#374151", group: "restoration" },
  amalgam:              { label: "Amalgam filling",      color: "#6B7280", group: "restoration" },
  crown:                { label: "Crown",                color: "#C89F55", whole: true, group: "prosthodontic" },
  implant:              { label: "Implant",              color: "#8A8CD9", whole: true, group: "surgical" },
  rct:                  { label: "Root canal treated",   color: "#7B4EA8", whole: true, group: "endodontic" },
  missing:              { label: "Missing",              color: "#EFEAE0", whole: true, group: "surgical" },
  extracted:            { label: "Extracted",            color: "#B94A48", whole: true, group: "surgical" },
  fracture:             { label: "Fracture",             color: "#E07A5F", group: "diagnosis" },
  mobility:             { label: "Mobility",             color: "#F2A65A", whole: true, group: "diagnosis" },
  bridge_abutment:      { label: "Bridge abutment",      color: "#4A7E62", whole: true, group: "prosthodontic" },
  pontic:               { label: "Pontic",               color: "#5F9E7E", whole: true, group: "prosthodontic" },
  veneer:               { label: "Veneer",               color: "#D9CBB8", group: "prosthodontic" },
  sealant:              { label: "Sealant",              color: "#5B9DB0", group: "preventive" },
  extraction_indicated: { label: "Extraction indicated", color: "#B33C3C", whole: true, group: "planned" },
  impacted:             { label: "Impacted",             color: "#5C4D3D", whole: true, group: "diagnosis" },
  retained_root:        { label: "Retained root",        color: "#8A6E4E", whole: true, group: "diagnosis" },
  calculus:             { label: "Calculus",             color: "#A69982", group: "periodontal" },
  gingival_recession:   { label: "Gingival recession",   color: "#D28B7A", group: "periodontal" },
  periapical_lesion:    { label: "Periapical lesion",    color: "#B0578C", whole: true, group: "diagnosis" },
  wear:                 { label: "Wear",                 color: "#B8AEA0", group: "diagnosis" },
  attrition:            { label: "Attrition",            color: "#B8AEA0", group: "diagnosis" },
  abrasion:             { label: "Abrasion",             color: "#B8AEA0", group: "diagnosis" },
  hypoplasia:           { label: "Hypoplasia",           color: "#8B7C4E", group: "diagnosis" },
  temp_restoration:     { label: "Temporary restoration",color: "#F4C97C", group: "restoration" },
};

export const SURFACE_LABELS = {
  M: "Mesial",
  D: "Distal",
  B: "Buccal / Labial",
  L: "Lingual / Palatal",
  O: "Occlusal",
  I: "Incisal",
};

// Small preview version of the tooth condition icon — for legends/menus.
export function ConditionIcon({ code, size = 22 }) {
  return (
    <svg viewBox="0 0 40 64" width={size} height={size * 1.3}>
      <CrownShape type={code === "crown" ? "molar" : "molar"} isPrimary={false}
                  fill="#FBF7EE" stroke="#B7AA8B" sw={0.7} />
      <RootShape type="molar" isPrimary={false} />
      <ConditionOverlays tooth={"16"} conditions={[{ code, surfaces: ["O"] }]} surfaces={[]} />
    </svg>
  );
}
